import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState({ payments_enabled: false, video_enabled: false, email_enabled: false, auto_verify: true });
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    // If we're mid Google sign-in (session_id present in the URL fragment), don't finalise as
    // "logged out" — the GoogleAuthGate will exchange it and set the token first.
    if (typeof window !== "undefined" && /session_id=/.test(window.location.hash || "")) {
      return;
    }
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const data = await api.me();
      setUser(data.user);
    } catch (e) {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.config().then(setConfig).catch(() => {});
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const data = await api.login({ email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const finishAuth = (data) => {
    if (data?.token) {
      setToken(data.token);
      setUser(data.user);
      setLoading(false);
    }
    return data;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const data = await api.googleSession(sessionId);
    setToken(data.token);
    setUser(data.user);
    setLoading(false);
    return data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const logoutEverywhere = async () => {
    // Invalidate all other sessions server-side; keep THIS device signed in with the fresh token.
    const data = await api.logoutEverywhere();
    if (data?.token) { setToken(data.token); setUser(data.user); }
    return data;
  };

  const refreshUser = async () => {
    const data = await api.me();
    setUser(data.user);
    return data.user;
  };

  const value = { user, setUser, config, loading, login, logout, logoutEverywhere, refreshUser, finishAuth, loginWithGoogleSession };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
